(function(global) {
  'use strict';

  var VERSION = 1;
  var STORAGE_KEY = 'abok_session_attribution_v1';
  var AJAX_SYNC_STORAGE_KEY = 'abok_session_attribution_ajax_sync_v1';
  var DEVICE_ID_STORAGE_KEY = 'abok_expose_device_id';
  var LAST_AD_TOUCH_STORAGE_KEY = 'abok_last_ad_touch_v1';
  var CART_DEVICE_ID_ATTRIBUTE = '_abok_device_id_v1';
  var CART_AD_TOUCH_ID_ATTRIBUTE = '_abok_ad_touch_id_v1';
  var AD_TOUCH_MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  var CART_ID_ATTRIBUTE = '_abok_attribution_id';
  var CART_DATA_ATTRIBUTE = '_abok_attribution_v1';
  var MAX_PARAMETER_LENGTH = 512;
  var MAX_PATH_LENGTH = 1024;
  var MAX_HOST_LENGTH = 255;
  var MAX_QUERY_KEY_LENGTH = 64;
  var MAX_UNKNOWN_QUERY_KEYS = 20;
  var MAX_CART_ATTRIBUTE_LENGTH = 4096;
  var UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
  var CLICK_ID_KEYS = [
    'fbclid',
    'gclid',
    'gad_source',
    'gbraid',
    'wbraid',
    'srsltid',
    'shpxid'
  ];
  var KNOWN_QUERY_KEYS = UTM_KEYS.map(function(key) {
    return 'utm_' + key;
  }).concat(CLICK_ID_KEYS);
  var snapshot = readStoredSnapshot() || captureSnapshot();
  var ajaxSyncPromise = null;
  var cachedDeviceId = '';
  var adTouchReported = false;
  var privacyReady = false;

  function truncate(value, maxLength) {
    return String(value || '').trim().slice(0, maxLength);
  }

  function normalizeKey(value) {
    return truncate(value, MAX_QUERY_KEY_LENGTH)
      .toLowerCase()
      .replace(/[^a-z0-9_.\[\]-]/g, '');
  }

  function getRandomId() {
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      return global.crypto.randomUUID();
    }

    return 'attribution-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function clone(value) {
    return value ? JSON.parse(JSON.stringify(value)) : null;
  }

  function getSessionStorage() {
    try {
      return global.sessionStorage || null;
    }
    catch (error) {
      return null;
    }
  }

  function getLocalStorage() {
    try {
      return global.localStorage || null;
    }
    catch (error) {
      return null;
    }
  }

  function getDeviceId() {
    if (global.Expose && typeof global.Expose.getUserId === 'function') {
      return global.Expose.getUserId();
    }
    if (cachedDeviceId) return cachedDeviceId;

    var storage = getLocalStorage();
    try {
      cachedDeviceId = storage && storage.getItem(DEVICE_ID_STORAGE_KEY) || getRandomId();
      if (storage) storage.setItem(DEVICE_ID_STORAGE_KEY, cachedDeviceId);
    }
    catch (error) {
      cachedDeviceId = getRandomId();
    }
    return cachedDeviceId;
  }

  function getAdTouchId() {
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      return global.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(character) {
      var random = Math.floor(Math.random() * 16);
      return (character === 'x' ? random : (random & 3 | 8)).toString(16);
    });
  }

  function getLastAdTouchId() {
    var storage = getLocalStorage();
    try {
      var value = storage && JSON.parse(storage.getItem(LAST_AD_TOUCH_STORAGE_KEY) || 'null');
      if (value && typeof value.event_id === 'string' && Date.now() - value.saved_at < AD_TOUCH_MAX_AGE && Date.now() >= value.saved_at) {
        return value.event_id;
      }
    }
    catch (error) {}
    return '';
  }

  function isMarketingAllowed() {
    var privacy = global.Shopify && global.Shopify.customerPrivacy;
    return Boolean(privacyReady && privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
  }

  function isPaidAdEntry(utm, clickIds) {
    var medium = String(utm.medium || '').toLowerCase();
    return ['cpc', 'ppc', 'paid_social', 'paid-social', 'paid_search', 'paid-search', 'display'].includes(medium) ||
      Boolean(clickIds.gclid || clickIds.gbraid || clickIds.wbraid);
  }

  function reportAdTouch() {
    if (adTouchReported || !isMarketingAllowed()) return;
    var locationUrl = getLocationUrl();
    var utm = readParameters(locationUrl.searchParams, UTM_KEYS, 'utm_');
    var clickIds = readParameters(locationUrl.searchParams, CLICK_ID_KEYS);
    if (!isPaidAdEntry(utm, clickIds)) return;

    adTouchReported = true;

    var eventId = getAdTouchId();
    var deviceId = getDeviceId();
    var occurredAt = new Date().toISOString();
    var storage = getLocalStorage();
    try {
      if (storage) storage.setItem(LAST_AD_TOUCH_STORAGE_KEY, JSON.stringify({ event_id: eventId, saved_at: Date.now() }));
    }
    catch (error) {}

    var action = {
      client_event_id: eventId,
      user_id: deviceId,
      trace_id: eventId,
      item_id: eventId,
      action_type: 'ad_touch',
      domain: 'extral',
      log_time: occurredAt,
      exinfo1: JSON.stringify({
        ad_touch: {
          version: 1,
          event_id: eventId,
          device_id: deviceId,
          occurred_at: occurredAt,
          utm: utm,
          click_ids: clickIds,
          landing_path: truncate(locationUrl.pathname || '/', MAX_PATH_LENGTH)
        }
      })
    };

    var report = global.Expose && typeof global.Expose.reportAction === 'function'
      ? global.Expose.reportAction(action, { keepalive: true })
      : global.fetch('https://storefront.abokpower.com/api/v1/actions/report', {
          method: 'POST',
          mode: 'no-cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({ actions: [action] })
        }).then(function(response) {
          if (response.type !== 'opaque' && !response.ok) {
            throw new Error('Ad touch report failed: ' + response.status);
          }
        });
    Promise.resolve(report).catch(function(error) {
      console.warn('Ad touch report failed:', error);
    });
  }

  function isValidSnapshot(value) {
    return Boolean(
      value &&
      value.version === VERSION &&
      typeof value.attribution_id === 'string' &&
      value.attribution_id &&
      value.utm &&
      value.click_ids &&
      typeof value.detected_source === 'string' &&
      typeof value.traffic_type === 'string' &&
      typeof value.detection_rule === 'string' &&
      typeof value.captured_at === 'string'
    );
  }

  function readStoredSnapshot() {
    var storage = getSessionStorage();
    if (!storage) return null;

    try {
      var value = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');

      if (isValidSnapshot(value)) {
        return value;
      }

      storage.removeItem(STORAGE_KEY);
      storage.removeItem(AJAX_SYNC_STORAGE_KEY);
    }
    catch (error) {
      try {
        storage.removeItem(STORAGE_KEY);
        storage.removeItem(AJAX_SYNC_STORAGE_KEY);
      }
      catch (storageError) {}
    }

    return null;
  }

  function writeSnapshot(value) {
    var storage = getSessionStorage();
    if (!storage) return;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(value));
    }
    catch (error) {}
  }

  function getLocationUrl() {
    try {
      return new URL(global.location.href);
    }
    catch (error) {
      return new URL('https://invalid.local/');
    }
  }

  function getExternalReferrerHost(locationUrl) {
    if (!global.document || !global.document.referrer) return '';

    try {
      var referrerUrl = new URL(global.document.referrer);
      return referrerUrl.origin === locationUrl.origin
        ? ''
        : truncate(referrerUrl.hostname.toLowerCase(), MAX_HOST_LENGTH);
    }
    catch (error) {
      return '';
    }
  }

  function getSearchEngine(host) {
    var hostname = String(host || '').toLowerCase();

    if (/(^|\.)google\./.test(hostname)) return 'google';
    if (/(^|\.)bing\.com$/.test(hostname)) return 'bing';
    if (/(^|\.)search\.yahoo\./.test(hostname)) return 'yahoo';
    if (/(^|\.)duckduckgo\.com$/.test(hostname)) return 'duckduckgo';
    if (/(^|\.)baidu\.com$/.test(hostname)) return 'baidu';
    if (/(^|\.)yandex\./.test(hostname)) return 'yandex';

    return '';
  }

  function readParameters(searchParams, keys, prefix) {
    return keys.reduce(function(values, key) {
      var parameterName = ((prefix || '') + key).toLowerCase();
      var parameterValue = '';

      searchParams.forEach(function(value, candidateName) {
        if (!parameterValue && candidateName.toLowerCase() === parameterName) {
          parameterValue = value;
        }
      });

      values[key] = truncate(parameterValue, MAX_PARAMETER_LENGTH);
      return values;
    }, {});
  }

  function getUnknownQueryKeys(searchParams) {
    var keys = [];

    searchParams.forEach(function(value, key) {
      var normalizedKey = normalizeKey(key);

      if (
        normalizedKey &&
        !KNOWN_QUERY_KEYS.includes(normalizedKey) &&
        !keys.includes(normalizedKey) &&
        keys.length < MAX_UNKNOWN_QUERY_KEYS
      ) {
        keys.push(normalizedKey);
      }
    });

    return keys;
  }

  function classifyAttribution(utm, clickIds, unknownQueryKeys, referrerHost, hasQueryMarker) {
    if (utm.source) {
      return {
        detected_source: utm.source,
        traffic_type: 'utm',
        detection_rule: 'utm_source'
      };
    }

    if (clickIds.fbclid) {
      return {
        detected_source: 'facebook',
        traffic_type: 'paid_social',
        detection_rule: 'fbclid'
      };
    }

    if (clickIds.gclid) {
      return {
        detected_source: 'google',
        traffic_type: 'paid_search',
        detection_rule: 'gclid'
      };
    }

    if (clickIds.gad_source) {
      return {
        detected_source: 'google',
        traffic_type: 'google_ads',
        detection_rule: 'gad_source'
      };
    }

    if (clickIds.gbraid || clickIds.wbraid) {
      return {
        detected_source: 'google',
        traffic_type: 'google_ads_privacy',
        detection_rule: clickIds.gbraid ? 'gbraid' : 'wbraid'
      };
    }

    if (clickIds.srsltid) {
      return {
        detected_source: 'google',
        traffic_type: 'google_merchant',
        detection_rule: 'srsltid'
      };
    }

    if (clickIds.shpxid) {
      return {
        detected_source: 'shopify',
        traffic_type: 'shopify_redirect',
        detection_rule: 'shpxid'
      };
    }

    if (unknownQueryKeys.length || hasQueryMarker) {
      return {
        detected_source: 'unknown',
        traffic_type: 'other_query',
        detection_rule: 'unrecognized_query'
      };
    }

    if (referrerHost) {
      var searchEngine = getSearchEngine(referrerHost);

      return {
        detected_source: searchEngine || referrerHost,
        traffic_type: searchEngine ? 'organic' : 'referral',
        detection_rule: 'external_referrer'
      };
    }

    return {
      detected_source: 'direct',
      traffic_type: 'direct',
      detection_rule: 'direct'
    };
  }

  function captureSnapshot() {
    var locationUrl = getLocationUrl();
    var searchParams = locationUrl.searchParams;
    var utm = readParameters(searchParams, UTM_KEYS, 'utm_');
    var clickIds = readParameters(searchParams, CLICK_ID_KEYS);
    var unknownQueryKeys = getUnknownQueryKeys(searchParams);
    var referrerHost = getExternalReferrerHost(locationUrl);
    var urlBeforeHash = locationUrl.href.split('#')[0];
    var hasQueryMarker = urlBeforeHash.includes('?');
    var classification = classifyAttribution(utm, clickIds, unknownQueryKeys, referrerHost, hasQueryMarker);
    var value = {
      version: VERSION,
      attribution_id: getRandomId(),
      utm: utm,
      click_ids: clickIds,
      detected_source: truncate(classification.detected_source, MAX_PARAMETER_LENGTH),
      traffic_type: classification.traffic_type,
      detection_rule: classification.detection_rule,
      landing_path: truncate(locationUrl.pathname || '/', MAX_PATH_LENGTH),
      referrer_host: referrerHost,
      captured_at: new Date().toISOString()
    };

    if (unknownQueryKeys.length) {
      value.unknown_query_keys = unknownQueryKeys;
    }

    writeSnapshot(value);
    return value;
  }

  function getSnapshot() {
    return clone(snapshot);
  }

  function getAttributionForAction() {
    var value = getSnapshot();
    if (!value) return null;

    value.attribution_origin = 'session_storage';
    return value;
  }

  function mergeIntoAction(action) {
    var payload = Object.assign({}, action || {});
    var attribution = getAttributionForAction();
    if (!attribution) return payload;

    var exinfo = {};

    if (payload.exinfo1 && typeof payload.exinfo1 === 'string') {
      try {
        exinfo = JSON.parse(payload.exinfo1);
      }
      catch (error) {
        return payload;
      }
    }
    else if (payload.exinfo1 && typeof payload.exinfo1 === 'object') {
      exinfo = Object.assign({}, payload.exinfo1);
    }

    exinfo.attribution = attribution;
    payload.exinfo1 = JSON.stringify(exinfo);
    return payload;
  }

  function serializeCartSnapshot() {
    var value = getSnapshot();
    if (!value) return '';

    var serialized = JSON.stringify(value);
    if (serialized.length <= MAX_CART_ATTRIBUTE_LENGTH) return serialized;

    delete value.unknown_query_keys;
    value.landing_path = truncate(value.landing_path, 512);
    value.referrer_host = truncate(value.referrer_host, 128);
    value.detected_source = truncate(value.detected_source, 128);
    value.utm.source = truncate(value.utm.source, 256);
    value.utm.medium = truncate(value.utm.medium, 256);
    value.utm.campaign = truncate(value.utm.campaign, 256);
    value.utm.term = truncate(value.utm.term, 128);
    value.utm.content = truncate(value.utm.content, 128);
    CLICK_ID_KEYS.forEach(function(key) {
      value.click_ids[key] = truncate(value.click_ids[key], 256);
    });
    serialized = JSON.stringify(value);

    if (serialized.length <= MAX_CART_ATTRIBUTE_LENGTH) return serialized;

    return JSON.stringify({
      version: value.version,
      attribution_id: value.attribution_id,
      utm: {
        source: truncate(value.utm.source, 128),
        medium: truncate(value.utm.medium, 128),
        campaign: truncate(value.utm.campaign, 128),
        term: '',
        content: ''
      },
      click_ids: CLICK_ID_KEYS.reduce(function(clickIds, key) {
        clickIds[key] = truncate(value.click_ids[key], 128);
        return clickIds;
      }, {}),
      detected_source: truncate(value.detected_source, 128),
      traffic_type: value.traffic_type,
      detection_rule: value.detection_rule,
      landing_path: '',
      referrer_host: '',
      captured_at: value.captured_at
    });
  }

  function getCartAttributes() {
    var value = getSnapshot();
    if (!value) return {};

    var attributes = {};
    attributes[CART_ID_ATTRIBUTE] = value.attribution_id;
    attributes[CART_DATA_ATTRIBUTE] = serializeCartSnapshot();
    attributes[CART_DEVICE_ID_ATTRIBUTE] = isMarketingAllowed() ? getDeviceId() : '';
    attributes[CART_AD_TOUCH_ID_ATTRIBUTE] = isMarketingAllowed() ? getLastAdTouchId() : '';
    return attributes;
  }

  function getAjaxCartUrl() {
    var root = global.Shopify && global.Shopify.routes && global.Shopify.routes.root
      ? global.Shopify.routes.root
      : '/';

    return root.replace(/\/$/, '') + '/cart/update.js';
  }

  function getAjaxSyncId() {
    var storage = getSessionStorage();
    if (!storage) return '';

    try {
      return storage.getItem(AJAX_SYNC_STORAGE_KEY) || '';
    }
    catch (error) {
      return '';
    }
  }

  function setAjaxSyncId(attributionId) {
    var storage = getSessionStorage();
    if (!storage) return;

    try {
      storage.setItem(AJAX_SYNC_STORAGE_KEY, attributionId);
    }
    catch (error) {}
  }

  function syncAjaxCart(options) {
    var config = options || {};
    var value = getSnapshot();
    if (!value || typeof global.fetch !== 'function') return Promise.resolve(null);
    var attributes = getCartAttributes();
    var syncKey = [value.attribution_id, attributes[CART_DEVICE_ID_ATTRIBUTE], attributes[CART_AD_TOUCH_ID_ATTRIBUTE]].join(':');
    if (!config.force && getAjaxSyncId() === syncKey) return Promise.resolve(null);
    if (ajaxSyncPromise) {
      return ajaxSyncPromise.then(function() { return syncAjaxCart(config); });
    }

    var controller = !config.signal && typeof global.AbortController === 'function'
      ? new global.AbortController()
      : null;
    var timeoutId = controller
      ? global.setTimeout(function() { controller.abort(); }, config.timeoutMs || 1500)
      : null;

    ajaxSyncPromise = global.fetch(config.url || getAjaxCartUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: config.keepalive === true,
      signal: config.signal || (controller && controller.signal),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: getCartAttributes()
      })
    }).then(function(response) {
      if (!response.ok) {
        throw new Error('Cart attribution update failed with status ' + response.status + '.');
      }

      setAjaxSyncId(syncKey);
      return response.json();
    }).finally(function() {
      if (timeoutId) global.clearTimeout(timeoutId);
      ajaxSyncPromise = null;
    });

    return ajaxSyncPromise;
  }

  global.AbokAttribution = {
    VERSION: VERSION,
    STORAGE_KEY: STORAGE_KEY,
    CART_ID_ATTRIBUTE: CART_ID_ATTRIBUTE,
    CART_DATA_ATTRIBUTE: CART_DATA_ATTRIBUTE,
    CART_DEVICE_ID_ATTRIBUTE: CART_DEVICE_ID_ATTRIBUTE,
    CART_AD_TOUCH_ID_ATTRIBUTE: CART_AD_TOUCH_ID_ATTRIBUTE,
    getSnapshot: getSnapshot,
    getCartAttributes: getCartAttributes,
    mergeIntoAction: mergeIntoAction,
    syncAjaxCart: syncAjaxCart
  };

  function syncInitialCartAttribution() {
    syncAjaxCart().catch(function(error) {
      console.warn('Initial cart attribution sync failed:', error);
    });
  }

  function startAdTouchTracking() {
    if (!global.Shopify || typeof global.Shopify.loadFeatures !== 'function') {
      syncInitialCartAttribution();
      return;
    }

    global.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function(error) {
      if (error) {
        syncInitialCartAttribution();
        return;
      }

      privacyReady = true;
      reportAdTouch();
      syncInitialCartAttribution();
      if (global.document) {
        global.document.addEventListener('visitorConsentCollected', function() {
          if (!isMarketingAllowed()) {
            try {
              var storage = getLocalStorage();
              if (storage) storage.removeItem(LAST_AD_TOUCH_STORAGE_KEY);
            }
            catch (storageError) {}
          }
          reportAdTouch();
          syncAjaxCart().catch(function(syncError) {
            console.warn('Ad attribution cart sync failed:', syncError);
          });
        });
      }
    });
  }

  if (global.document && global.document.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', startAdTouchTracking, { once: true });
  }
  else {
    startAdTouchTracking();
  }
})(typeof window !== 'undefined' ? window : globalThis);
